import { Head, Link } from "@inertiajs/react"

import AppLayout from "@/layouts/app-layout"
import { formatDate } from "@/lib/date-utils"
import type { BreadcrumbItem } from "@/types"

interface Customer {
  id: number
  display_name: string
}

interface Document {
  id: number
  name: string | null
  document_date: string | null
  document_type: string | null
  filename: string | null
  filetype: string | null
  download_url: string | null
  created_at: string
}

interface Props {
  customer: Customer
  documents: Document[]
  can_create: boolean
}

const FILE_ICONS: Record<string, string> = {
  pdf: "\u{1F4C4}",
  doc: "\u{1F4DD}",
  docx: "\u{1F4DD}",
  xls: "\u{1F4CA}",
  xlsx: "\u{1F4CA}",
  jpg: "\u{1F5BC}",
  jpeg: "\u{1F5BC}",
  png: "\u{1F5BC}",
  gif: "\u{1F5BC}",
  mp3: "\u{1F3B5}",
  mp4: "\u{1F3AC}",
}

function fileIcon(filetype: string | null): string {
  if (!filetype) return "\u{1F4CE}"
  return FILE_ICONS[filetype.toLowerCase()] ?? "\u{1F4CE}"
}

export default function Index({ customer, documents, can_create }: Props) {
  const breadcrumbs: BreadcrumbItem[] = [
    { title: "Customers", href: "/customers" },
    { title: customer.display_name, href: `/customers/${customer.id}` },
    { title: "Documents", href: `/customers/${customer.id}/documents` },
  ]

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={`Documents - ${customer.display_name}`} />

      <div className="p-6">
        <div className="mb-4 flex items-center justify-between">
          <h1 className="text-2xl font-semibold">
            Documents for {customer.display_name}
          </h1>
          {can_create && (
            <Link
              href={`/customers/${customer.id}/documents/new`}
              className="bg-hpi-dark-blue hover:bg-hpi-blue rounded px-4 py-2 text-white"
            >
              Add Document
            </Link>
          )}
        </div>

        {documents.length > 0 ? (
          <table className="border-hpi-light-gray w-full border-collapse border">
            <thead>
              <tr className="bg-hpi-pale-gray">
                <th className="border-hpi-light-gray border px-4 py-2 text-left">
                  Date
                </th>
                <th className="border-hpi-light-gray border px-4 py-2 text-left">
                  Type
                </th>
                <th className="border-hpi-light-gray border px-4 py-2 text-left">
                  Description
                </th>
                <th className="border-hpi-light-gray w-16 border px-4 py-2 text-center">
                  File
                </th>
              </tr>
            </thead>
            <tbody>
              {documents.map((doc) => (
                <tr key={doc.id} className="hover:bg-hpi-pale-blue">
                  <td className="border-hpi-light-gray border px-4 py-2">
                    {formatDate(doc.document_date)}
                  </td>
                  <td className="border-hpi-light-gray border px-4 py-2">
                    {doc.document_type}
                  </td>
                  <td className="border-hpi-light-gray border px-4 py-2">
                    <Link
                      href={`/customers/${customer.id}/documents/${doc.id}`}
                      className="text-hpi-active-blue hover:underline"
                    >
                      {doc.name ?? "(No description)"}
                    </Link>
                  </td>
                  <td className="border-hpi-light-gray border px-4 py-2 text-center">
                    {doc.download_url ? (
                      <a
                        href={doc.download_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-hpi-active-blue text-lg hover:underline"
                        title={doc.filename ?? "View document"}
                      >
                        {fileIcon(doc.filetype)}
                      </a>
                    ) : (
                      <span className="text-hpi-med-gray text-lg">
                        {fileIcon(doc.filetype)}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p className="text-hpi-med-gray">No documents found.</p>
        )}

        <div className="mt-4">
          <Link
            href={`/customers/${customer.id}`}
            className="text-hpi-active-blue hover:text-hpi-active-blue"
          >
            Back to Customer
          </Link>
        </div>
      </div>
    </AppLayout>
  )
}
